import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Send, UserCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import { DespachoCountBadge, DespachoInfoCallout, DespachoSectionCard } from "@/components/despacho/DespachoUi";
import {
  CLIENT_INBOX_FILTERS,
  CLIENT_INBOX_QUICK_ACTIONS,
  DESPACHO_CLIENT_INBOX_SUBTITLE,
  DESPACHO_CLIENT_INBOX_TITLE,
  DESPACHO_CLIENT_MODULE_REQUIRED_NOTE,
  clientInboxStatusTone,
  filterClientInboxConversations,
} from "@/modules/despacho/despachoChatUi";
import { mockClientInboxConversations } from "@/modules/despacho/despachoMockData";

const STATUS_BADGE = {
  info: "bg-sky-100 text-sky-900 border-sky-200",
  warn: "bg-amber-100 text-amber-900 border-amber-200",
  neutral: "bg-slate-100 text-slate-800 border-slate-200",
};

const PRIORITY_BADGE = {
  Crítico: "bg-rose-100 text-rose-900 border-rose-200",
  Urgente: "bg-amber-100 text-amber-900 border-amber-200",
  Alta: "bg-sky-100 text-sky-900 border-sky-200",
  Media: "bg-slate-100 text-slate-800 border-slate-200",
};

/**
 * Bandeja compartida de atención al cliente — estilo WhatsApp Business (mock).
 * @param {{ viewerRole?: string, viewerStaffId?: string, embedded?: boolean, initialConversationId?: string, despachoModuleActive?: boolean }} props
 */
export default function DespachoClientInboxPanel({
  viewerRole = "owner",
  viewerStaffId = "",
  embedded = false,
  initialConversationId = "",
  despachoModuleActive = true,
}) {
  const [filterId, setFilterId] = useState("todos");
  const [selectedId, setSelectedId] = useState(initialConversationId || mockClientInboxConversations[0]?.id || "");
  const [replyDraft, setReplyDraft] = useState("");
  const [localMessages, setLocalMessages] = useState({});

  const visibleRows = useMemo(
    () => filterClientInboxConversations(mockClientInboxConversations, filterId, { viewerRole, viewerStaffId }),
    [filterId, viewerRole, viewerStaffId],
  );

  const selected = useMemo(
    () => visibleRows.find((r) => r.id === selectedId) || visibleRows[0] || null,
    [visibleRows, selectedId],
  );

  const messages = useMemo(() => {
    if (!selected) return [];
    const extra = localMessages[selected.id] || [];
    return [...(selected.messages || []), ...extra];
  }, [selected, localMessages]);

  const handleSendReply = () => {
    const text = replyDraft.trim();
    if (!text || !selected) return;
    setLocalMessages((prev) => ({
      ...prev,
      [selected.id]: [
        ...(prev[selected.id] || []),
        {
          id: `out-${Date.now()}`,
          from: viewerRole === "auxiliar" ? "Auxiliar" : "Despacho",
          text,
          at: "Ahora",
          inbound: false,
        },
      ],
    }));
    setReplyDraft("");
  };

  const inboxBody = (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(240px,320px)_1fr] gap-4 min-h-[320px]">
      <div className="space-y-2">
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="Filtros bandeja clientes">
          {CLIENT_INBOX_FILTERS.map((f) => (
            <Button
              key={f.id}
              type="button"
              size="sm"
              variant={filterId === f.id ? "secondary" : "ghost"}
              className="rounded-sm h-7 text-[10px] px-2"
              onClick={() => setFilterId(f.id)}
              data-testid={`despacho-client-filter-${f.id}`}
            >
              {f.label}
            </Button>
          ))}
        </div>

        <ul className="space-y-1.5 m-0 p-0 list-none max-h-[360px] overflow-y-auto" data-testid="despacho-client-inbox-list">
          {visibleRows.length === 0 ? (
            <li className="text-xs text-slate-500 px-2 py-4">No hay conversaciones en este filtro.</li>
          ) : (
            visibleRows.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(row.id)}
                  className={`w-full text-left rounded-sm border px-3 py-2 text-xs transition-colors ${
                    selected?.id === row.id
                      ? "border-[#0F2D5C] bg-[#0F2D5C]/5"
                      : "border-slate-200 bg-white hover:bg-slate-50"
                  }`}
                  data-testid={`despacho-client-thread-${row.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-[#0F2D5C] m-0 truncate">{row.client_name}</p>
                      <p className="text-[10px] text-slate-500 m-0">{row.rfc || "—"}</p>
                    </div>
                    {(row.unread_count || 0) > 0 ? (
                      <DespachoCountBadge value={row.unread_count} tone="info" />
                    ) : null}
                  </div>
                  <p className="text-[11px] text-slate-600 m-0 mt-1 truncate">{row.last_message}</p>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    <Badge
                      variant="outline"
                      className={`text-[9px] font-normal ${STATUS_BADGE[clientInboxStatusTone(row.status)]}`}
                    >
                      {row.status}
                    </Badge>
                    {row.priority ? (
                      <Badge variant="outline" className={`text-[9px] font-normal ${PRIORITY_BADGE[row.priority] || PRIORITY_BADGE.Media}`}>
                        {row.priority}
                      </Badge>
                    ) : null}
                  </div>
                  <p className="text-[10px] text-slate-400 m-0 mt-1">{row.last_contact_at}</p>
                </button>
              </li>
            ))
          )}
        </ul>
      </div>

      <div className="rounded-sm border border-slate-200 bg-white flex flex-col min-h-[280px]" data-testid="despacho-client-inbox-detail">
        {selected ? (
          <>
            <div className="border-b border-slate-100 px-4 py-3 space-y-1">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-bold text-[#0F2D5C] m-0">{selected.client_name}</p>
                  <p className="text-[11px] text-slate-500 m-0">RFC: {selected.rfc || "—"}</p>
                </div>
                <Badge variant="outline" className={`text-[10px] font-normal ${STATUS_BADGE[clientInboxStatusTone(selected.status)]}`}>
                  {selected.status}
                </Badge>
              </div>
              <p className="text-[11px] text-slate-600 m-0 flex items-center gap-1">
                <UserCircle2 className="w-3.5 h-3.5" aria-hidden />
                Responsable: <span className="font-medium">{selected.assignee || "Sin asignar"}</span>
                · Prioridad: {selected.priority || "—"}
              </p>
              <p className="text-[10px] text-slate-400 m-0">Último contacto: {selected.last_contact_at}</p>
            </div>

            <ul className="flex-1 overflow-y-auto space-y-2 m-0 p-3 list-none bg-slate-50/50" data-testid="despacho-client-inbox-messages">
              {messages.map((msg) => (
                <li
                  key={msg.id}
                  className={`rounded-sm px-3 py-2 text-xs max-w-[85%] ${
                    msg.inbound
                      ? "mr-auto bg-white border border-slate-200"
                      : "ml-auto bg-emerald-700 text-white"
                  }`}
                >
                  <span className={`block text-[10px] font-semibold mb-0.5 ${msg.inbound ? "text-slate-500" : "text-white/80"}`}>
                    {msg.from}
                  </span>
                  <p className="m-0 leading-relaxed">{msg.text}</p>
                  <span className={`block text-[10px] mt-1 ${msg.inbound ? "text-slate-400" : "text-white/70"}`}>{msg.at}</span>
                </li>
              ))}
            </ul>

            <div className="border-t border-slate-100 p-3 space-y-2">
              <div className="flex flex-wrap gap-1">
                {CLIENT_INBOX_QUICK_ACTIONS.slice(0, 6).map((label) => (
                  <Button
                    key={label}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-sm h-7 text-[9px] px-2"
                    title="Acción demostrativa — Fase 2"
                    data-testid={`despacho-client-action-${label.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => {}}
                  >
                    {label}
                  </Button>
                ))}
              </div>
              <div className="flex gap-2 items-end">
                <SpellcheckTextarea
                  wrapperClassName="flex-1 min-w-0"
                  value={replyDraft}
                  onChange={(e) => setReplyDraft(e.target.value)}
                  placeholder="Respuesta al cliente (demostración)…"
                  className="min-h-[56px] text-xs rounded-sm resize-none"
                  testId="despacho-client-inbox-composer"
                />
                <Button
                  type="button"
                  size="sm"
                  className="rounded-sm h-auto self-end bg-[#3DA873] hover:bg-[#349966] text-white"
                  onClick={handleSendReply}
                  data-testid="despacho-client-inbox-send"
                >
                  <Send className="w-3.5 h-3.5" aria-hidden />
                </Button>
              </div>
              <p className="text-[10px] text-slate-500 m-0">
                Continuidad del despacho: cualquier auxiliar autorizado puede responder aunque cambie la asignación.
              </p>
            </div>
          </>
        ) : (
          <p className="text-xs text-slate-500 p-6 m-0">Seleccione una conversación de cliente.</p>
        )}
      </div>
    </div>
  );

  const moduleRequiredNote = (
    <DespachoInfoCallout testId="despacho-client-module-required">
      {DESPACHO_CLIENT_MODULE_REQUIRED_NOTE}
    </DespachoInfoCallout>
  );

  if (!despachoModuleActive) {
    if (embedded) {
      return (
        <section className="space-y-3" data-testid="despacho-client-inbox-panel">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xs font-bold text-[#0F2D5C] m-0">{DESPACHO_CLIENT_INBOX_TITLE}</h3>
          </div>
          <p className="text-[10px] text-slate-500 m-0">{DESPACHO_CLIENT_INBOX_SUBTITLE}</p>
          {moduleRequiredNote}
        </section>
      );
    }

    return (
      <DespachoSectionCard
        title={DESPACHO_CLIENT_INBOX_TITLE}
        description={DESPACHO_CLIENT_INBOX_SUBTITLE}
        testId="despacho-client-inbox-panel"
      >
        {moduleRequiredNote}
      </DespachoSectionCard>
    );
  }

  if (embedded) {
    return (
      <section className="space-y-3" data-testid="despacho-client-inbox-panel">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-xs font-bold text-[#0F2D5C] m-0">{DESPACHO_CLIENT_INBOX_TITLE}</h3>
          <Button asChild variant="link" size="sm" className="h-auto p-0 text-[11px] text-[#0F2D5C]">
            <Link to="/despacho/mensajes?tab=clientes">Abrir bandeja completa</Link>
          </Button>
        </div>
        <p className="text-[10px] text-slate-500 m-0">{DESPACHO_CLIENT_INBOX_SUBTITLE}</p>
        {inboxBody}
      </section>
    );
  }

  return (
    <DespachoSectionCard
      title={DESPACHO_CLIENT_INBOX_TITLE}
      description={DESPACHO_CLIENT_INBOX_SUBTITLE}
      testId="despacho-client-inbox-panel"
    >
      {inboxBody}
    </DespachoSectionCard>
  );
}
